import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const DB_NAME = 'StockFilterDB';
const DB_VERSION = 1;
const STORE_NAME = 'stocks';

let db = null;

// Initialize IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        objectStore.createIndex('symbol', 'symbol', { unique: false });
        objectStore.createIndex('sector', 'sector', { unique: false });
        objectStore.createIndex('industry', 'industry', { unique: false });
        objectStore.createIndex('signal_score', 'signal_score', { unique: false });
        objectStore.createIndex('trend_state', 'trend_state', { unique: false });
      }
    };
  });
};

// Load CSV data into IndexedDB
export const loadCSVData = async (csvPath) => {
  try {
    await initDB();
    
    const response = await fetch(csvPath);
    const csvText = await response.text();
    
    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const objectStore = transaction.objectStore(STORE_NAME);
            
            // Clear existing data
            await objectStore.clear();
            
            // Map and insert data
            let count = 0;
            for (const row of results.data) {
              const mappedRow = mapCSVRow(row);
              if (mappedRow.symbol) {
                objectStore.add(mappedRow);
                count++;
              }
            }
            
            transaction.oncomplete = () => resolve({ success: true, count });
            transaction.onerror = () => reject(transaction.error);
          } catch (error) {
            reject(error);
          }
        },
        error: (error) => reject(error)
      });
    });
  } catch (error) {
    console.error('Error loading CSV:', error);
    throw error;
  }
};

// Map CSV row to database format
const mapCSVRow = (row) => {
  const parseNum = (val) => val === '' || val === undefined ? null : parseFloat(val);
  const parseInt = (val) => val === '' || val === undefined ? null : Number.parseInt(val);
  
  return {
    symbol: row.symbol,
    signal_score: parseInt(row.signal_score),
    predicted_signal_score: parseInt(row.predicted_signal_score),
    predicted_direction: row.predicted_direction,
    n_days_since_cross_over: parseInt(row.n_days_since_cross_over),
    new_up_trend: parseInt(row.new_up_trend),
    new_down_trend: parseInt(row.new_down_trend),
    trend_state: parseInt(row.trend_state),
    trend_comment: row.trend_comment,
    n_last_downtrend_duration: parseInt(row.n_last_downtrend_duration),
    pcnt_upside_in_range_6m: parseNum(row.pcnt_upside_in_range_6m),
    pcnt_upside_in_range_12m: parseNum(row.pcnt_upside_in_range_12m),
    gap_from_6m_high: row.gap_from_6m_high,
    gap_from_12m_high: row.gap_from_12m_high,
    duration_range: row.duration_range,
    near_period_vwma20_low: parseInt(row.near_period_vwma20_low),
    rsi: parseNum(row.rsi),
    is_etf: parseInt(row.isEtf),
    short_fname: row.short_fname,
    long_fname: row.long_fname,
    company_name: row.companyName,
    industry: row.industry,
    sector: row.sector,
    return_on_equity_ttm: parseNum(row.returnOnEquityTTM),
    price_sales_ratio_ttm: parseNum(row.priceSalesRatioTTM),
    price_earnings_ratio_ttm: parseNum(row.priceEarningsRatioTTM),
    price_earnings_to_growth_ratio_ttm: parseNum(row.priceEarningsToGrowthRatioTTM),
    gross_profit_margin_ttm: parseNum(row.grossProfitMarginTTM),
    net_profit_margin_ttm: parseNum(row.netProfitMarginTTM),
    debt_ratio_ttm: parseNum(row.debtRatioTTM),
    piotroski_score: parseInt(row.piotroskiScore),
    eps_pos_qtrs: parseInt(row.eps_pos_qtrs),
    revenue_1yr_growth: parseNum(row.revenue_1yr_growth),
    revenue_3yr_growth: parseNum(row.revenue_3yr_growth),
    forecast_next_year_revenue_growth_rate: parseNum(row.forecast_next_year_revenue_growth_rate),
    gross_profit_1yr_growth: parseNum(row.grossProfit_1yr_growth),
    gross_profit_3yr_growth: parseNum(row.grossProfit_3yr_growth),
    forecast_next_year_profit_growth_rate: parseNum(row.forecast_next_year_profit_growth_rate),
    eps_cagr: parseNum(row.eps_cagr),
    sma_10_200: row.sma_10_200,
    sma_30_200: row.sma_30_200,
    sma_50_200: row.sma_50_200,
    z_score_200d: parseNum(row.z_score_200d),
    z_score_60d: parseNum(row.z_score_60d),
    z_score_21d: parseNum(row.z_score_21d),
    sma50_slope: parseInt(row.sma50_slope),
    sma200_slope: parseInt(row.sma200_slope),
    dcf: parseNum(row.dcf),
    price_to_dcf_ratio: parseNum(row.price_to_dcf_ratio),
    rule_of_40: parseNum(row.rule_of_40),
    quant_ratings: parseNum(row['Quant Ratings']),
    abc_flag: parseNum(row.ABC_flag),
    rating_recommendation: row.ratingRecommendation,
    holdings: row.holdings
  };
};

// Format record for frontend
const formatRecord = (record) => ({
  id: record.id,
  symbol: record.symbol,
  signal_score: record.signal_score,
  predicted_signal_score: record.predicted_signal_score,
  predicted_direction: record.predicted_direction,
  n_days_since_cross_over: record.n_days_since_cross_over,
  new_up_trend: record.new_up_trend,
  new_down_trend: record.new_down_trend,
  trend_state: record.trend_state,
  trend_comment: record.trend_comment,
  n_last_downtrend_duration: record.n_last_downtrend_duration,
  pcnt_upside_in_range_6m: record.pcnt_upside_in_range_6m,
  pcnt_upside_in_range_12m: record.pcnt_upside_in_range_12m,
  gap_from_6m_high: record.gap_from_6m_high,
  gap_from_12m_high: record.gap_from_12m_high,
  duration_range: record.duration_range,
  near_period_vwma20_low: record.near_period_vwma20_low,
  rsi: record.rsi,
  isEtf: record.is_etf,
  short_fname: record.short_fname,
  long_fname: record.long_fname,
  companyName: record.company_name,
  industry: record.industry,
  sector: record.sector,
  returnOnEquityTTM: record.return_on_equity_ttm,
  priceSalesRatioTTM: record.price_sales_ratio_ttm,
  priceEarningsRatioTTM: record.price_earnings_ratio_ttm,
  priceEarningsToGrowthRatioTTM: record.price_earnings_to_growth_ratio_ttm,
  grossProfitMarginTTM: record.gross_profit_margin_ttm,
  netProfitMarginTTM: record.net_profit_margin_ttm,
  debtRatioTTM: record.debt_ratio_ttm,
  piotroskiScore: record.piotroski_score,
  eps_pos_qtrs: record.eps_pos_qtrs,
  revenue_1yr_growth: record.revenue_1yr_growth,
  revenue_3yr_growth: record.revenue_3yr_growth,
  forecast_next_year_revenue_growth_rate: record.forecast_next_year_revenue_growth_rate,
  gross_profit_1yr_growth: record.gross_profit_1yr_growth,
  gross_profit_3yr_growth: record.gross_profit_3yr_growth,
  forecast_next_year_profit_growth_rate: record.forecast_next_year_profit_growth_rate,
  eps_cagr: record.eps_cagr,
  sma_10_200: record.sma_10_200,
  sma_30_200: record.sma_30_200,
  sma_50_200: record.sma_50_200,
  z_score_200d: record.z_score_200d,
  z_score_60d: record.z_score_60d,
  z_score_21d: record.z_score_21d,
  sma50_slope: record.sma50_slope,
  sma200_slope: record.sma200_slope,
  dcf: record.dcf,
  price_to_dcf_ratio: record.price_to_dcf_ratio,
  rule_of_40: record.rule_of_40,
  is_etf: record.is_etf,
  holdings: record.holdings
});

// Apply filters (reuse from SupabaseService)
const applyFilters = (records, filters) => {
  if (Object.keys(filters).length === 0) return records;
  
  return records.filter(record => {
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        if (key === 'trend_state') {
          if (value === 'up_turning_down') {
            if (record.trend_state !== 1 || record.predicted_signal_score !== 0) return false;
          } else if (value === 'down_turning_up') {
            if (record.trend_state !== 0 || record.predicted_signal_score !== 5) return false;
          } else if (typeof value === 'string') {
            const numericValue = Number.parseInt(value);
            if (!isNaN(numericValue) && record[key] !== numericValue) return false;
          } else if (typeof value === 'number') {
            if (record[key] !== value) return false;
          }
        } else if (key === 'new_trend') {
          if (value && value !== '') {
            const dayRange = Number.parseInt(value);
            const nDays = record.n_days_since_cross_over;
            if (nDays === null || nDays === undefined || nDays < -dayRange || nDays > dayRange) return false;
          }
        } else if (key === 'pcnt_upside_in_range_6m') {
          if (value && value !== '') {
            const threshold = parseFloat(value);
            const recordValue = record.pcnt_upside_in_range_6m;
            if (recordValue === null || recordValue === undefined || recordValue <= threshold) return false;
          }
        } else if (['sma_10_200', 'sma_30_200', 'sma_50_200'].includes(key)) {
          if (value && value !== '') {
            const smaValue = record[key];
            if (!smaValue) return false;
            const lowerValue = smaValue.toLowerCase();
            if (value === 'above' && !lowerValue.includes('above')) return false;
            if (value === 'below' && !lowerValue.includes('below')) return false;
          }
        } else if (['z_score_200d', 'z_score_60d', 'z_score_21d'].includes(key)) {
          if (value && value !== '') {
            const zValue = record[key];
            if (zValue === null || zValue === undefined) return false;
            if (value === 'gt_3' && zValue <= 3) return false;
            if (value === 'gt_2' && zValue <= 2) return false;
            if (value === 'gt_1' && zValue <= 1) return false;
            if (value === 'gt_0' && zValue <= 0) return false;
            if (value === 'lt_0' && zValue >= 0) return false;
            if (value === 'lt_-1' && zValue >= -1) return false;
            if (value === 'lt_-2' && zValue >= -2) return false;
            if (value === 'lt_-3' && zValue >= -3) return false;
          }
        } else if (key === 'rsi') {
          if (value && value !== '') {
            const rsiValue = record.rsi;
            if (rsiValue === null || rsiValue === undefined) return false;
            if (value === 'gt_80' && rsiValue <= 80) return false;
            if (value === 'gt_70' && rsiValue <= 70) return false;
            if (value === 'gt_60' && rsiValue <= 60) return false;
            if (value === 'gt_50' && rsiValue <= 50) return false;
            if (value === 'lt_50' && rsiValue >= 50) return false;
            if (value === 'lt_40' && rsiValue >= 40) return false;
            if (value === 'lt_30' && rsiValue >= 30) return false;
            if (value === 'lt_20' && rsiValue >= 20) return false;
          }
        } else if (key === 'symbol') {
          const normalizedValue = value.replace(/[\s\t;]+/g, ',');
          const symbols = normalizedValue.split(',').map(s => s.trim().toUpperCase()).filter(s => s.length > 0);
          if (symbols.length === 0) return true;
          const recordSymbol = record[key] ? record[key].toString().toUpperCase() : '';
          if (!symbols.includes(recordSymbol)) return false;
        } else if (typeof value === 'string') {
          if (!record[key] || !record[key].toString().toLowerCase().includes(value.toLowerCase())) return false;
        } else if (typeof value === 'number') {
          if (record[key] !== value) return false;
        }
      }
    }
    return true;
  });
};

// Get trend category for a stock
const getTrendCategory = (stock) => {
  const trendState = parseInt(stock.trend_state);
  const signalScore = parseInt(stock.signal_score);
  const predictedScore = parseInt(stock.predicted_signal_score);

  if (trendState === 1) {
    // UP Trend
    if (signalScore >= 3 && predictedScore >= 3) {
      return 'uptrend_strong';
    } else if (signalScore < 3 && predictedScore < 3) {
      return 'uptrend_weakening';
    } else if (signalScore >= 3 && predictedScore === 0) {
      return 'uptrend_near_reversal';
    } else {
      return 'uptrend_other';
    }
  } else {
    // DOWN Trend
    if (signalScore < 3 && predictedScore < 3) {
      return 'downtrend_declining';
    } else if (signalScore > 3 && predictedScore > 3) {
      return 'downtrend_improving';
    } else if (signalScore > 3 && predictedScore === 5) {
      return 'downtrend_near_reversal';
    } else {
      return 'downtrend_other';
    }
  }
};

// Apply sorting to records with trend category support
const applySorting = (records, sortBy) => {
  if (!sortBy) return records;
  
  // Check if this is a trend category sort
  const trendCategories = [
    'uptrend_strong',
    'uptrend_weakening',
    'uptrend_near_reversal',
    'downtrend_declining',
    'downtrend_improving',
    'downtrend_near_reversal'
  ];

  if (trendCategories.includes(sortBy)) {
    // Sort by trend category - matching category first, then others
    return [...records].sort((a, b) => {
      const aCat = getTrendCategory(a);
      const bCat = getTrendCategory(b);
      
      // If a matches the target category, put it first
      if (aCat === sortBy && bCat !== sortBy) return -1;
      if (aCat !== sortBy && bCat === sortBy) return 1;
      
      // If both match or both don't match, sort by signal score descending
      return parseInt(b.signal_score) - parseInt(a.signal_score);
    });
  }
  
  // Standard sorting for other fields
  const [field, direction] = sortBy.split('_').reduce((acc, part, idx, arr) => {
    if (part === 'asc' || part === 'desc') {
      return [arr.slice(0, idx).join('_'), part];
    }
    return acc;
  }, [sortBy, 'desc']);
  
  return [...records].sort((a, b) => {
    let aVal = a[field];
    let bVal = b[field];
    
    // Handle null/undefined values
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    // Numeric comparison
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // String comparison
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    
    if (direction === 'asc') {
      return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
    } else {
      return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
    }
  });
};

// Get all records with filters, sorting, and pagination
export const getRecords = async (filters = {}, page = 1, pageSize = 18, sortBy = 'signal_score_desc') => {
  if (!db) await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.getAll();
    
    request.onsuccess = () => {
      const allRecords = request.result.map(formatRecord);
      const filteredRecords = applyFilters(allRecords, filters);
      const sortedRecords = applySorting(filteredRecords, sortBy);
      
      const totalCount = sortedRecords.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      const offset = (page - 1) * pageSize;
      const paginatedRecords = sortedRecords.slice(offset, offset + pageSize);
      
      resolve({
        data: paginatedRecords,
        page,
        pageSize,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
        isEstimatedCount: false,
        totalCount,
        totalPages
      });
    };
    
    request.onerror = () => reject(request.error);
  });
};

// Get record by ID
export const getRecordById = async (id) => {
  if (!db) await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.get(Number(id));
    
    request.onsuccess = () => resolve(request.result ? formatRecord(request.result) : null);
    request.onerror = () => reject(request.error);
  });
};

// Load CSV data from a File object (for admin upload)
export const loadFromFileObject = async (file) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      Papa.parse(e.target.result, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const objectStore = transaction.objectStore(STORE_NAME);
            objectStore.clear();
            let count = 0;
            for (const row of results.data) {
              const mapped = mapCSVRow(row);
              if (mapped.symbol) { objectStore.add(mapped); count++; }
            }
            transaction.oncomplete = () => resolve({ success: true, recordCount: count });
            transaction.onerror = () => reject(transaction.error);
          } catch (err) { reject(err); }
        },
        error: reject
      });
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

// Load Excel data from a File object (for admin upload)
export const loadFromExcel = async (file) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(STORE_NAME);
        objectStore.clear();
        let count = 0;
        for (const row of rows) {
          const mapped = mapCSVRow(row);
          if (mapped.symbol) { objectStore.add(mapped); count++; }
        }
        transaction.oncomplete = () => resolve({ success: true, recordCount: count });
        transaction.onerror = () => reject(transaction.error);
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Check if data exists
export const hasData = async () => {
  if (!db) await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.count();
    
    request.onsuccess = () => resolve(request.result > 0);
    request.onerror = () => reject(request.error);
  });
};

export default {
  loadCSVData,
  loadFromFileObject,
  loadFromExcel,
  getRecords,
  getRecordById,
  hasData
};
